import React, { FC, memo, useState, useRef } from 'react';
import { RichText } from '@montana/shared/rich-text';
import { Form, FormTextInput, FormCheckbox, FormSelect } from '@montana/shared/form';
import { useTranslations } from '@montana/containers/translations';
import { required, combineValidators, email } from '@montana/shared/form/validation';
import { Button } from '@montana/shared/button';
import { Translate } from '@montana/shared/translate';
import { IM17Newsletter } from '@montana/models/generated/cms.interface';
import { subscribe } from '@montana/services/newsletter';
import { IFormValues, IFormRef } from '@montana/shared/form/form.component';
import { Appear } from '@montana/shared/appear';
import { IFieldOptionValue } from '@montana/shared/form/field/field';
import { useSettings } from '@montana/containers/settings/settings.container';
import { usePage } from '@montana/containers/page';
import { useTracking } from '@montana/containers/tracking';
import { useConnectedServiceFunctions } from '@montana/shared/use-connected-service-functions';
import { useDynamicSettings } from '@montana/containers/dynamic-settings';
import clsx from 'clsx';

export const M17NewsletterSignUpBlock: FC<IM17Newsletter> = memo((props: IM17Newsletter) => {
    const fullNameInput = 'fullName';
    const emailInput = 'email';
    const customerTypeInput = 'customerType';
    const acceptTermsInput = 'acceptTerms';

    const { title, text, termsAndPrivacyPolicy, backgroundColorHex } = props;
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [subscribed, setSubscribed] = useState<boolean | undefined>();
    const { name } = usePage();
    const { market } = useSettings();
    const { trackCustomEvent } = useTracking();
    const { customerTypes, cfCountry } = useDynamicSettings();
    const translate = useTranslations();
    const [cSubscribe] = useConnectedServiceFunctions(subscribe);

    const customerType = !!customerTypes?.length ? customerTypes[0].key : undefined;
    const formRef = useRef<IFormRef>(null);

    async function handleSubmit(values: IFormValues) {
        setSubmitting(true);
        setSubscribed(undefined);

        try {
            trackCustomEvent('newsletter', 'sign up', document.location.pathname);

            const response = await cSubscribe({
                fullName: values[fullNameInput],
                email: values[emailInput],
                customerType: values[customerTypeInput] || customerType,
                country: cfCountry || market?.marketId!,
                language: market?.language!,
                signupSource: `Sign up block - Page: ${name}`
            });

            if (response.ok) {
                setSubscribed(true);
                formRef.current?.getForm()?.resetForm({
                    [fullNameInput]: '',
                    [emailInput]: '',
                    [customerTypeInput]: customerType,
                    [acceptTermsInput]: false,
                });
            } else {
                throw new Error("Subscription unsuccessful");
            }
        } catch (error) {
            setSubscribed(false);
        } finally {
            setSubmitting(false);
            setTimeout(() => {
                setSubscribed(undefined);
            }, 3000);
        }
    }

    return (
        <Appear>
            <section
                className="newsletter-sign-up"
                style={{ backgroundColor: backgroundColorHex }}
            >
                {title && <RichText className="newsletter-sign-up__title" text={title} marginOnLastChild={!!text} />}
                <div className="newsletter-sign-up__content">
                    {text && <RichText className="newsletter-sign-up__text" text={text} />}
                    <div className="newsletter-sign-up-form">
                        <Form onSubmit={handleSubmit} initialValues={{}} ref={formRef}>
                            {() => (
                                <>
                                    <FormTextInput
                                        type="text"
                                        name={fullNameInput}
                                        label={translate(t => t.forms.fullName, '[Full name]')}
                                        validate={required(translate(t => t.forms.fieldRequired, '[This field is required]'))}
                                        required
                                    />
                                    <FormTextInput
                                        type="email"
                                        name={emailInput}
                                        label={translate(t => t.forms.email, '[E-mail]')}
                                        validate={combineValidators(
                                            required(translate(t => t.forms.fieldRequired, '[This field is required]')),
                                            email(translate(t => t.forms.invalidEmail, '[Invalid email]'))
                                        )}
                                        required
                                    />
                                    {!!customerTypes?.length && (
                                        <FormSelect
                                            name={customerTypeInput}
                                            options={customerTypes.map<IFieldOptionValue>(c => ({ text: c.displayName, value: c.key! }))}
                                            defaultValue={customerType}
                                            label={translate(t => t.forms.customerType, '[Customer type]')}
                                            inlineLabel={true}
                                            required
                                        />
                                    )}
                                    <FormCheckbox
                                        className="newsletter-sign-up__accept-terms"
                                        name={acceptTermsInput}
                                        validate={required(translate(t => t.forms.mustBeChecked, '[This must be checked]'))}
                                    >
                                        {termsAndPrivacyPolicy &&
                                            <RichText className="newsletter-sign-up__accept-terms-text" text={termsAndPrivacyPolicy} disableAppear />
                                        }
                                    </FormCheckbox>
                                    <Button
                                        className="newsletter-sign-up__submit-button"
                                        submitButton
                                        isLoading={submitting}
                                    >
                                        <Translate value="forms.signMeUp" defaultValue="[Sign me up]" />
                                    </Button>
                                    <div className={clsx("newsletter-sign-up-form__feedback", subscribed !== undefined && "newsletter-sign-up-form__feedback--active")}>
                                        <span className={subscribed ? "newsletter-sign-up-form__subscription-success" : "newsletter-sign-up-form__subscription-failed"}>
                                            {subscribed
                                                ? <Translate value="general.subscribed" defaultValue="You have succesfully subscribed to the newsletter!" />
                                                : subscribed !== undefined && <Translate value="general.notSubscribed" defaultValue="Failed to subscribe to the newsletter" />
                                            }
                                        </span>
                                    </div>
                                </>
                            )}
                        </Form>
                    </div>
                </div>
            </section>
        </Appear >
    );
});
